// @file The recommended actions card as Rails serves it (RecommendedActions::CardSerializer), and the helpers its
// links and completions share. Rails picks the actions; the client renders them.
import { UserAccountType, type RecommendedActionsCardExperimentVariant } from '@@/enums'
import type { LearnVideo } from '@@/pinia/dash_learn_store'
import { Audience } from '@@/types/dash'

type RecommendedActionsRoleSource = 'account_type' | 'tenant_role' | 'library_access' | 'fallback'

type ThumbnailImage = LearnVideo['video']['thumbnail']['light']

interface RecommendedActionSession {
  id: string
  title: string
  durationMinutes: number | null
  url: string
  thumbnailUrl: string | null
}

interface RecommendedActionLearnVideo {
  // Untranslated: the Learn page opens a video by it.
  skill: string
  translatedSkill: string | null
  video: {
    videoId: string
    videoDurationInSeconds: number
    thumbnailAsset: ThumbnailImage | null
    thumbnail: { light: ThumbnailImage; dark: ThumbnailImage }
  }
}

interface RecommendedActionTemplate {
  galleryTemplateId: string
  title: string
  description: string | null
  thumbnail: { url: string | null }
}

type RecommendedAction =
  | { kind: 'padlet_tv'; key: string; session: RecommendedActionSession }
  // `video` is null when Learn data failed to load; the link then falls back to the Padlet 101 section.
  | { kind: 'learn_video'; key: string; video: RecommendedActionLearnVideo | null }
  | { kind: 'learn_page'; key: 'learn_page' }
  | { kind: 'gallery_template'; key: string; template: RecommendedActionTemplate }
  | { kind: 'browse_gallery'; key: 'browse_gallery' }

// The padlet.tv PD course pinned below More to try. The client words its title by role.
interface PinnedPdAction {
  kind: 'pd_course'
  key: string
  session: Pick<RecommendedActionSession, 'id' | 'url'>
}

interface RecommendedActionsCard {
  variant: RecommendedActionsCardExperimentVariant
  role: UserAccountType
  roleSource: RecommendedActionsRoleSource
  // Whether a reload can feature something else.
  canReload: boolean
  // The key to send as the cursor when the user reloads, which features upNext.
  nextCursor: string
  // The user's active days and reloads so far, for analytics.
  activeDay: number
  reloads: number
  // The teacher's profile grades and subjects; null for any other role.
  teacherProfile: { grades: string[]; subjects: string[] } | null
  featured: RecommendedAction
  upNext: RecommendedAction
  moreToTry: RecommendedAction[]
  pdAction: PinnedPdAction | null
}

function audienceForRole(role: UserAccountType): Audience {
  switch (role) {
    case UserAccountType.TEACHER:
    case UserAccountType.STAFF:
    case UserAccountType.STUDENT:
      return Audience.Education
    case UserAccountType.BUSINESS:
      return Audience.Business
    default:
      return Audience.General
  }
}

// The key a Learn video's action is completed under, as Rails keys it in the card. Get started is keyed by its Learn
// anchor.
function learnVideoActionKey(video: LearnVideo, getStartedVideo: LearnVideo | null | undefined): string {
  if (video.video.videoId === getStartedVideo?.video.videoId) return 'learn:padlet-101'
  return `learn:${video.video.videoId}`
}

export { audienceForRole, learnVideoActionKey }
export type {
  PinnedPdAction,
  RecommendedAction,
  RecommendedActionLearnVideo,
  RecommendedActionsCard,
  RecommendedActionsRoleSource,
  RecommendedActionTemplate,
}
